/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import Frame from '../widgets/Frame';
import { isFeatureOn } from "../../portalSettings";

/**
 * "כבר יש לכם חשבון" - המסך שנכנס במקום `user_already_exists`.
 *
 * ## למה מסך ולא הודעת שגיאה
 *
 * ההרשמה נעצרה, אבל לנרשם **לא חסר דבר**: יש לו חשבון, והוא הרגע הוכיח את הטלפון
 * ואת המייל שרשומים עליו - כלומר הוא בדיוק מי שיכול להיכנס אליו בהתחברות מהירה,
 * עכשיו, בלי סיסמה ובלי פנייה אלינו. שורת שגיאה אדומה מול טופס הרשמה אומרת לו את
 * ההפך: שמשהו אצלו לא בסדר, ושאין לו מה לעשות.
 *
 * זה בולט במיוחד בחשבון שנפתח בהתחברות מהירה עצמה - חשבון ללא סיסמה. `registerNewUser`
 * אינו עוצר אותו (הוא עוצר רק מי שיש לו שם משתמש), ולכן הוא הגיע עד הסוף ונדחה
 * בשורה האחרונה, אחרי שני קודי אימות.
 *
 * ## הדרך השנייה
 *
 * "להירשם עם פרטים אחרים" מחזיר להרשמה. זו התשובה למי שהקיש בטעות את הטלפון של בן
 * משפחה: לא נחסמה בפניו ההרשמה, נאמר לו שהפרטים האלה כבר תפוסים.
 *
 * ריסיילר שההתחברות המהירה כבויה אצלו מקבל את אותו מסך בלי הכפתור הראשון - נשארת
 * ההתחברות הרגילה, שממנה יש מסלול שחזור.
 */
export function AccountExists() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const reseller = useSelector((state) => state.reseller.value);
    const quickLoginAvailable = isFeatureOn(reseller, 'allowQuickLogin');

    return (
            <Frame>
                <div className='main main-account-exists'>
                    <div className='main-top'>
                        <h1>{t("You already have an account")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                <h2>{t("The phone number and email address you just verified are already registered with us.")}</h2>
                                {quickLoginAvailable ? (
                                    <>
                                        <h2>{t("You can sign in right now with a one-time code - no username and no password.")}</h2>
                                        <div className='button button-space-between button-teal mt30' onClick={() => navigate('/login')}>
                                            <div className='mr20'>{t("Sign in with a one-time code")}</div>
                                            <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <h2>{t("Please sign in to your existing account.")}</h2>
                                        <div className='button button-space-between button-teal mt30' onClick={() => navigate('/login')}>
                                            <div className='mr20'>{t("Go to login")}</div>
                                            <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>
                                        </div>
                                    </>
                                )}
                                <h2 className='teal pointer mt20' onClick={() => navigate('/register')}>{t("Register with different details")}</h2>
                            </div>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-pointing.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
    );
}
